import { Checkbox, Grid, Skeleton } from '@mui/material';
import { HSpacer, RoutalPalette, RoutalSearchInput, RoutalTooltip } from '@nx-smartmonkey/ui';
import _ from 'lodash';
import { useIntl } from 'react-intl';

import { PaginatedResult } from '@nx-smartmonkey/shared/interfaces';
import DialogInfo from '../../../../assets/svg/DialogInfo';
import { Vehicle } from '../../../../domain/vehicle/Vehicle';
import Text from '../../../../react-components/Text';
import { AlignmentTypes, DataTable } from '../../../../react-components/data-table/DataTable';
import { VehicleAvatar } from '../../../shared/VehicleAvatar';
import { CreatePlanAllVehiclesObject } from './CreatePlanModal';

type VehiclesSelectionProps = {
  allVehicles: CreatePlanAllVehiclesObject[];
  paginatedVehicles?: PaginatedResult<Vehicle>;
  isLoading?: boolean;
  setOffset: (offset: number) => void;
  setFilterText: (text: string) => void;
  setSelectedVehicles: (event?: CreatePlanAllVehiclesObject[]) => void;
  executionDate: Date;
  maxVehicleNameLength?: number;
};

export const VehiclesSelection = ({
  allVehicles,
  paginatedVehicles,
  isLoading = false,
  setOffset,
  setFilterText,
  setSelectedVehicles,
  executionDate,
  maxVehicleNameLength = 30,
}: VehiclesSelectionProps) => {
  const intl = useIntl();

  const selectedVehicles = allVehicles.filter((vehicle) => vehicle.selected).map((vehicle) => vehicle.vehicle);

  const { docs: vehicles, limit, page, pages } = paginatedVehicles || {};

  return (
    <div style={{ height: `100%`, width: `100%` }}>
      <Grid container direction="column" style={{ height: `100%`, width: `100%` }} wrap="nowrap" gap="8px">
        <Grid container direction="row" alignItems="center" justifyContent="space-between" wrap="nowrap">
          <Grid item container alignItems="center">
            {isLoading ? (
              <Skeleton variant="rectangular" animation="pulse" height={20} width={160} />
            ) : (
              <Text
                variant="body"
                intlId={`${selectedVehicles.length > 0 ? `vehicles.header.selected_info` : `vehicles.header.num_rows`}`}
                intlValues={{
                  num: selectedVehicles.length,
                  total: allVehicles.length ?? ``,
                }}
              />
            )}
          </Grid>

          <Grid container style={{ height: `100%`, width: `100%` }} justifyContent="flex-end">
            <Grid item>
              <Grid container direction="column" justifyContent="center" style={{ height: `100%` }}>
                <RoutalSearchInput
                  inputContainerStyles={{
                    width: `240px`,
                    borderRadius: `10px`,
                  }}
                  bGColor="white"
                  onChange={_.debounce((text: any) => {
                    setFilterText(text);
                  }, 300)}
                  searchLive
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <div style={{ height: `calc(100% - 36px)` }}>
          <DataTable
            loading={isLoading}
            styledTableRowStyles={{
              selectedBackgroundColor: `transparent`,
            }}
            docs={(vehicles ?? [])
              .filter((vehicle) => vehicle.enabled)
              .map((vehicle) => ({
                doc: vehicle,
                checkboxIcon: {
                  element: (
                    <Checkbox
                      style={{
                        height: `24px`,
                        width: `24px`,
                        opacity: !selectedVehicles.some((v) => v.id === vehicle.id) ? 0.3 : 1,
                      }}
                      checked={false}
                    />
                  ),
                  end: true,
                  showAlways: true,
                },
                disabled: !selectedVehicles.some((v) => v.id === vehicle.id),
              }))}
            // maxHeight={`${dataMaxHeight}px`}
            total={allVehicles.length ?? 0}
            limit={limit}
            page={page}
            pages={pages}
            showCheckboxColumnRight={true}
            selectedIds={selectedVehicles.map((vehicle) => vehicle.id)}
            noFooter={(pages ?? 0) <= 1}
            emptyMessage={<Text intlId="no_results" />}
            onChangePage={({ offset, limit }) => {
              setOffset(offset);
            }}
            onMultipleSelection={({ selectedIds, fromGlobalCheckbox }) => {
              if (fromGlobalCheckbox) {
                if (selectedVehicles.length > 0) {
                  const allVehiclesAfterSelection = allVehicles.map((vehicle) => ({
                    vehicle: vehicle.vehicle,
                    selected: false,
                  }));
                  setSelectedVehicles(allVehiclesAfterSelection);
                } else {
                  const paginatedVehiclesIds = vehicles?.map((vehicle) => vehicle.id) ?? [];
                  const allVehiclesAfterSelection = allVehicles.map((vehicle) => ({
                    vehicle: vehicle.vehicle,
                    selected: paginatedVehiclesIds.includes(vehicle.vehicle.id),
                  }));
                  setSelectedVehicles(allVehiclesAfterSelection);
                }
              } else {
                const paginatedVehiclesIds = vehicles?.map((vehicle) => vehicle.id) ?? [];
                // If the vehicle isin the paginated list, the id might appear in the selectedIds list
                // If it is paginated but doesnt appear in the list, it means it is not selected,
                // If the vehicleId is paginated and appears, it means it is selected.
                // If it is not paginated, keep the previous value.
                const allVehiclesAfterSelection = allVehicles.map((vehicle) => ({
                  vehicle: vehicle.vehicle,
                  selected: paginatedVehiclesIds.includes(vehicle.vehicle.id)
                    ? (selectedIds ?? []).includes(vehicle.vehicle.id)
                    : vehicle.selected,
                }));
                setSelectedVehicles(allVehiclesAfterSelection);
              }
            }}
            onRowOneClick={(vehicleRow: { id: string }) => {
              const allVehiclesAfterSelection = allVehicles.map((vehicle) => ({
                vehicle: vehicle.vehicle,
                selected: vehicleRow.id === vehicle.vehicle.id ? !vehicle.selected : vehicle.selected,
              }));
              setSelectedVehicles(allVehiclesAfterSelection);
            }}
            columns={[
              {
                accessorName: `label`,
                name: <Text variant="label" intlId="vehicles.data.details.label" />,
                formatter: ({ doc }: { doc: Vehicle }) => (
                  <Grid container direction="row" style={{ height: `30px`, whiteSpace: `nowrap` }} alignItems="center">
                    <Grid item alignItems="center" display={`flex`}>
                      <VehicleAvatar
                        vehicleId={doc.id}
                        vehicleColor={doc.color?.value}
                        additionalStyles={{ height: `20px` }}
                      />
                    </Grid>

                    <HSpacer small />

                    <Grid item>
                      {doc.label && doc.label !== `` ? (
                        <Text variant="body">
                          {doc.label.length > maxVehicleNameLength
                            ? `${doc.label.substring(0, maxVehicleNameLength)}...`
                            : doc.label}
                        </Text>
                      ) : (
                        <Text variant="body" style={{ color: RoutalPalette.secondary00 }} intlId="no_name" />
                      )}
                    </Grid>
                  </Grid>
                ),
              },
              {
                accessorName: `default_working_days`,
                align: `right` as AlignmentTypes,
                maxWidth: `50px`,
                formatter: ({ value: default_working_days, doc }: { value: string[]; doc: Vehicle }) => {
                  if (
                    default_working_days &&
                    default_working_days.length > 0 &&
                    selectedVehicles.some((vehicle) => vehicle.id === doc.id) &&
                    !default_working_days.includes(Vehicle.getWeekdayByDate(executionDate))
                  ) {
                    return (
                      <RoutalTooltip title={intl.formatMessage({ id: `planner.create_plan.vehicle_not_working` })}>
                        <Grid style={{ display: `flex`, justifyContent: `center` }}>
                          <DialogInfo style={{ color: RoutalPalette.secondary40, height: `18px`, width: `18px` }} />
                        </Grid>
                      </RoutalTooltip>
                    );
                  }
                  return null;
                },
              },
            ]}
          />
        </div>
      </Grid>
    </div>
  );
};
